// ============================================================
// Simulasi Harga Cetak — cari produk → variasi → banding POS/WEB/APP
// ============================================================

import axios from '../../config/axios.js';
import Alpine from 'alpinejs';

function createKasirPriceSimulation() {
  let lineKey = 0;

  return {
    productSearch: '',
    productOptions: [],
    draft: {
      product: null,
      qty: 1,
      widthCm: '',
      heightCm: '',
      variants: [],
    },
    lines: [],
    result: null,
    loading: false,
    errorMsg: '',
    customerPhone: '',
    copyOk: false,

    init() {},

    needsSize(product) {
      if (!product) return false;
      const unit = (product.price_unit || '').toLowerCase();
      return unit === 'meter' || product.is_custom_size;
    },

    /** Group variants by type for clearer UI */
    variantGroups(product) {
      const list = product?.variants || [];
      const map = {};
      list.forEach((v) => {
        const t = v.type || 'Lainnya';
        if (!map[t]) map[t] = [];
        map[t].push(v);
      });
      return Object.keys(map).map((type) => ({ type, items: map[type] }));
    },

    channelPriceHint(product) {
      if (!product) return '';
      const fmt = (n) =>
        n == null ? '—' : 'Rp ' + Number(n).toLocaleString('id-ID');
      const unit = (product.price_unit || '').toLowerCase() === 'meter';
      if (unit) {
        return (
          'POS ' +
          fmt(product.price_per_m2 ?? product.base_price) +
          '/m² · Web ' +
          fmt(product.web_price_per_m2 ?? product.web_price) +
          '/m² · App ' +
          fmt(product.app_price_per_m2 ?? product.app_price) +
          '/m²'
        );
      }
      return (
        'POS ' +
        fmt(product.base_price) +
        ' · Web ' +
        fmt(product.web_price) +
        ' · App ' +
        fmt(product.app_price)
      );
    },

    async searchProducts() {
      this.productOptions = [];
      const q = this.productSearch.trim();
      if (q.length < 2) return;
      try {
        const { data } = await axios.get('/api/v1/kasir/products', {
          params: { search: q, status: 'active', per_page: 15 },
        });
        this.productOptions = data.data || [];
      } catch {
        this.productOptions = [];
      }
    },

    async selectProduct(summary) {
      try {
        const { data } = await axios.get('/api/v1/kasir/products/' + summary.id);
        this.draft.product = data.data;
        this.draft.qty = Math.max(1, this.draft.product.min_order || 1);
        this.draft.widthCm = '';
        this.draft.heightCm = '';
        this.draft.variants = [];
        this.productSearch = this.draft.product.name;
        this.productOptions = [];
      } catch {
        window.toast?.('Gagal memuat detail produk.', 'error');
      }
    },

    toggleVariant(id, checked) {
      if (checked) {
        if (!this.draft.variants.includes(id)) {
          this.draft.variants.push(id);
        }
      } else {
        this.draft.variants = this.draft.variants.filter((v) => v !== id);
      }
    },

    isVariantChecked(id) {
      return this.draft.variants.includes(id);
    },

    selectedVariantLabels() {
      const p = this.draft.product;
      if (!p) return [];
      return (p.variants || [])
        .filter((v) => this.draft.variants.includes(v.id))
        .map((v) => (v.type || '') + ': ' + (v.value || ''));
    },

    addLine() {
      const p = this.draft.product;
      if (!p) return;

      if (this.needsSize(p) && (!this.draft.widthCm || !this.draft.heightCm)) {
        window.toast?.('Lebar dan tinggi (cm) wajib diisi.', 'warning');
        return;
      }

      const labels = this.selectedVariantLabels();
      lineKey += 1;
      this.lines.push({
        key: lineKey,
        product_id: p.id,
        product_name: p.name,
        sku: p.sku || '',
        price_unit: p.price_unit,
        qty: Math.max(1, parseInt(this.draft.qty, 10) || 1),
        custom_width_cm: this.needsSize(p) ? Number(this.draft.widthCm) : null,
        custom_height_cm: this.needsSize(p) ? Number(this.draft.heightCm) : null,
        variants: [...this.draft.variants],
        variants_label: labels.join(', '),
        channel_hint: this.channelPriceHint(p),
      });

      this.clearDraft();
      this.result = null;
      this.errorMsg = '';
      this.copyOk = false;
    },

    clearDraft() {
      this.draft = { product: null, qty: 1, widthCm: '', heightCm: '', variants: [] };
      this.productSearch = '';
      this.productOptions = [];
    },

    removeLine(idx) {
      this.lines.splice(idx, 1);
      this.result = null;
      this.copyOk = false;
    },

    linePreview(line) {
      const parts = [`Qty: ${line.qty}`];
      if (line.custom_width_cm && line.custom_height_cm) {
        parts.push(`${line.custom_width_cm}×${line.custom_height_cm} cm`);
      }
      if (line.variants_label) {
        parts.push(line.variants_label);
      }
      return parts.join(' · ');
    },

    async calculate() {
      if (this.lines.length === 0) {
        window.toast?.('Tambahkan produk terlebih dahulu.', 'warning');
        return;
      }
      this.loading = true;
      this.errorMsg = '';
      this.result = null;
      this.copyOk = false;
      try {
        const payload = {
          compare_channels: true,
          items: this.lines.map(
            ({ product_id, qty, custom_width_cm, custom_height_cm, variants }) => ({
              product_id,
              qty,
              ...(custom_width_cm ? { custom_width_cm, custom_height_cm } : {}),
              variants,
            }),
          ),
        };
        const { data } = await axios.post('/api/v1/pos/price-simulation', payload);
        this.result = data.data;
      } catch (e) {
        this.errorMsg = e.response?.data?.message || 'Gagal menghitung simulasi.';
        window.toast?.(this.errorMsg, 'error');
      } finally {
        this.loading = false;
      }
    },

    async copyShareText() {
      const text = this.result?.share_text;
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        this.copyOk = true;
        window.toast?.('Teks simulasi disalin. Siap kirim ke pelanggan.', 'success');
        setTimeout(() => (this.copyOk = false), 2500);
      } catch {
        window.toast?.('Gagal menyalin. Salin manual dari kotak teks.', 'error');
      }
    },

    openWhatsApp() {
      let url = this.result?.share_whatsapp_url;
      if (!url) return;
      const phone = (this.customerPhone || '').replace(/\D/g, '');
      if (phone) {
        let n = phone;
        if (n.startsWith('0')) n = '62' + n.slice(1);
        if (!n.startsWith('62')) n = '62' + n;
        const text = encodeURIComponent(this.result.share_text || '');
        url = `https://wa.me/${n}?text=${text}`;
      }
      window.open(url, '_blank', 'noopener');
    },

    resetAll() {
      this.lines = [];
      this.result = null;
      this.errorMsg = '';
      this.copyOk = false;
      this.customerPhone = '';
      this.clearDraft();
    },

    fmtRP(n) {
      return Number(n || 0).toLocaleString('id-ID');
    },
  };
}

Alpine.data('kasirPriceSimulation', () => createKasirPriceSimulation());
